import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ClockIcon, HomeIcon } from '@heroicons/react/20/solid'
import { format } from 'date-fns'
import { JSONTree } from 'react-json-tree'

import Loader from '../components/Loader'
import Button from '../components/Button'
import FlagFormModal from '../components/experiments/FlagFormModal'
import StatusActionModal from '../components/experiments/StatusActionModal'
import ExperimentResults from '../components/experiments/ExperimentResults'
import {
  KindBadge,
  LockNotice,
  Notice,
  StatusBadge
} from '../components/experiments/ExperimentBadges'
import { ACTIONS_HELP, Explain, HELP } from '../components/experiments/Help'
import { mutation } from '../api'
import { extractErrorMessage } from '../helper'
import { STAGE } from '../helper/stage'
import {
  ACTIONS,
  KIND,
  METRIC_LABELS,
  SURFACE_LABELS,
  UNIT_LABELS,
  actionLabel,
  allowedActions,
  applyStatusAction,
  currentSalt,
  daysRunning,
  describeAllocation,
  evaluationCronInput,
  formatNumber,
  formatPercent,
  isAlreadyExistsError,
  isLocked,
  needsEvaluationCron,
  resultFor
} from '../helper/experiments'
import {
  PricingWriteCancelledError,
  guardPricingFlagSave
} from '../helper/pricing-flag'
import { useNotificationContext } from '../context/notification-context'
import {
  CONFLICT_MESSAGE,
  FlagsConflictError,
  useFeatureFlags
} from '../hooks/use-feature-flags'

const ACTION_SUCCESS = {
  [ACTIONS.START]: 'Gestartet',
  [ACTIONS.PAUSE]: 'Pausiert',
  [ACTIONS.RESUME]: 'Wieder aufgenommen',
  [ACTIONS.CONCLUDE]: 'Beendet',
  [ACTIONS.RESTART]: 'Neu gestartet'
}

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null
  return date && !Number.isNaN(date.getTime())
    ? format(date, 'dd.MM.yyyy HH:mm')
    : '-'
}

const describeTargeting = (targeting = {}) => {
  const parts = []
  if (targeting.stages?.length)
    parts.push(`Stages ${targeting.stages.join(', ')}`)
  if (targeting.partner_ids?.length) {
    parts.push(`${targeting.partner_ids.length} Partner-IDs`)
  }
  if (targeting.lead_ampel?.length) {
    parts.push(`Ampel ${targeting.lead_ampel.join(', ')}`)
  }
  if (typeof targeting.is_trial === 'boolean') {
    parts.push(targeting.is_trial ? 'nur Trial' : 'nur ohne Trial')
  }
  return parts.length ? parts.join(' · ') : 'Keine Einschränkung'
}

const Card = ({ title, description, help, actions, children }) => (
  <section className='mt-4 bg-white shadow sm:rounded-lg'>
    <div className='flex flex-row items-start justify-between gap-4 px-4 py-5 sm:px-6'>
      <div>
        <h2 className='text-lg font-medium leading-6 text-gray-900'>{title}</h2>
        {description && (
          <p className='mt-1 max-w-2xl text-sm text-gray-500'>{description}</p>
        )}
        {help && <Explain className='mt-2 max-w-2xl'>{help}</Explain>}
      </div>
      {actions}
    </div>
    <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>{children}</div>
  </section>
)

const Item = ({ label, children, mono }) => (
  <div>
    <dt className='text-sm font-medium text-gray-500'>{label}</dt>
    <dd
      className={`mt-1 break-words text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}
    >
      {children}
    </dd>
  </div>
)

// Prozent eines Rollouts, jederzeit änderbar.
const PercentControl = ({ flag, onSave, isSaving, disabled }) => {
  const [percent, setPercent] = useState(Number(flag.percent) || 0)
  const stored = Number(flag.percent) || 0
  const clamped = Math.min(100, Math.max(0, Math.round(Number(percent) || 0)))

  return (
    <div>
      <div className='flex flex-wrap items-center gap-4'>
        <input
          type='range'
          min='0'
          max='100'
          step='1'
          value={clamped}
          onChange={(e) => setPercent(e.target.value)}
          className='min-w-[12rem] flex-1 accent-indigo-600'
          aria-label='Prozent als Slider'
          disabled={disabled}
        />
        <input
          type='number'
          min='0'
          max='100'
          step='1'
          value={percent}
          onChange={(e) => setPercent(e.target.value)}
          className='w-24 rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm'
          aria-label='Prozent'
          disabled={disabled}
        />
        <span className='text-sm text-gray-500'>%</span>
        <Button.PrimaryLG
          text='Prozent speichern'
          onClick={() => onSave(clamped)}
          isLoading={isSaving}
          disabled={disabled || clamped === stored}
        />
      </div>
      <p className='mt-2 text-xs text-gray-500'>
        Gespeichert sind {formatNumber(stored)} %. Hochdrehen behält alle, die
        schon drin sind.
        {flag.status !== 'running' &&
          ' Solange der Rollout nicht läuft, bekommen alle off oder den Gewinner.'}
      </p>
    </div>
  )
}

const Breadcrumb = ({ flagKey }) => (
  <nav className='flex mb-8' aria-label='Breadcrumb'>
    <ol className='flex items-center space-x-4'>
      <li>
        <Link to='/' className='text-gray-400 hover:text-gray-500'>
          <HomeIcon className='h-5 w-5 flex-shrink-0' aria-hidden='true' />
          <span className='sr-only'>Home</span>
        </Link>
      </li>
      {[
        { name: 'Experimente', to: '/experiments' },
        { name: flagKey, to: '#', current: true }
      ].map((page) => (
        <li key={page.name}>
          <div className='flex items-center'>
            <svg
              className='h-5 w-5 flex-shrink-0 text-gray-300'
              fill='currentColor'
              viewBox='0 0 20 20'
              aria-hidden='true'
            >
              <path d='M5.555 17.776l8-16 .894.448-8 16-.894-.448z' />
            </svg>
            <Link
              to={page.to}
              className='ml-4 text-sm font-medium text-gray-500 hover:text-gray-700'
              aria-current={page.current ? 'page' : undefined}
            >
              {page.name}
            </Link>
          </div>
        </li>
      ))}
    </ol>
  </nav>
)

const Experiment = () => {
  const { key } = useParams()
  const { success, error, warning } = useNotificationContext()
  const {
    flagsQuery,
    resultsQuery,
    saveFlags: rawSaveFlags
  } = useFeatureFlags()
  // pricing_enforcement auf production nur mit doppelter Bestätigung, wie im
  // Override-Control der Kundenseite
  const saveFlags = useMemo(
    () => guardPricingFlagSave(rawSaveFlags, { stage: STAGE }),
    [rawSaveFlags]
  )

  const [formOpen, setFormOpen] = useState(false)
  const [formSession, setFormSession] = useState(null)
  const [actionOpen, setActionOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  // Wendet eine Änderung auf den aktuell geladenen Stand an und speichert.
  // Konflikte (z. B. Evaluator hat gerade beendet) laden neu und melden es.
  const writeFlag = async (change, successMessage) => {
    setIsSaving(true)
    try {
      const base = flagsQuery.data
      const current = { ...base.flags[key], key }
      await saveFlags(base, { ...base.flags, [key]: change(current) })
      success(successMessage)
      return true
    } catch (err) {
      if (err instanceof PricingWriteCancelledError) warning(err.message)
      else if (err instanceof FlagsConflictError) error(CONFLICT_MESSAGE)
      else error(`Speichern fehlgeschlagen. ${extractErrorMessage(err)}`)
      return false
    } finally {
      setIsSaving(false)
    }
  }

  if (flagsQuery.isLoading) {
    return <Loader loadingText='Flag wird geladen ...' />
  }

  if (flagsQuery.isError) {
    return (
      <div>
        <Breadcrumb flagKey={key} />
        <Notice tone='error'>
          FEATURE_FLAGS konnte nicht geladen werden.{' '}
          {extractErrorMessage(flagsQuery.error)}
        </Notice>
      </div>
    )
  }

  const base = flagsQuery.data
  const stored = base.flags[key]

  if (!stored) {
    return (
      <div>
        <Breadcrumb flagKey={key} />
        <div className='bg-white shadow sm:rounded-lg px-4 py-8 text-center'>
          <h2 className='text-sm font-semibold text-gray-900'>
            Flag nicht gefunden
          </h2>
          <p className='mt-1 text-sm text-gray-500'>
            In FEATURE_FLAGS gibt es kein Flag mit dem Key {key}.
          </p>
          <Link
            to='/experiments'
            className='mt-4 inline-block text-sm font-medium text-indigo-600 hover:text-indigo-500'
          >
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    )
  }

  const flag = { ...stored, key }
  const isExperiment = flag.kind === KIND.EXPERIMENT
  const result = resultFor(resultsQuery.data, key)
  const locked = isLocked(flag)
  const days = daysRunning(flag)
  const readOnly = base.parseError
  const overrides = Object.entries(flag.overrides || {})

  const openEdit = () => {
    setFormSession({ id: Date.now(), base, flagKey: key })
    setFormOpen(true)
  }

  const openAction = (action) => {
    setPendingAction({ id: Date.now(), action, flag, result })
    setActionOpen(true)
  }

  // Legt den nächsten Termin der täglichen Auswertung an. Existiert er schon,
  // ist alles gut. Andere Fehler sind nur eine Warnung, der Start gilt.
  const ensureEvaluationCron = async () => {
    try {
      await mutation({
        mutation: 'createCronTTL',
        input: evaluationCronInput(new Date())
      })
    } catch (err) {
      if (isAlreadyExistsError(err)) return
      warning(
        `Gestartet, aber der Termin für die tägliche Auswertung konnte nicht angelegt werden. ${extractErrorMessage(err)}`
      )
    }
  }

  const confirmAction = async ({ winner, salt }) => {
    const { action, flag: actionFlag } = pendingAction
    const ok = await writeFlag(
      (current) =>
        applyStatusAction(current, action, { now: new Date(), winner, salt }),
      ACTION_SUCCESS[action]
    )
    if (!ok) return
    setActionOpen(false)
    if (needsEvaluationCron(actionFlag, action)) await ensureEvaluationCron()
  }

  const savePercent = (percent) =>
    writeFlag(
      (current) => ({ ...current, percent }),
      `Rollout auf ${percent} % gesetzt`
    )

  return (
    <div>
      <Breadcrumb flagKey={key} />

      <header className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <div className='flex flex-wrap items-center gap-3'>
            <h1 className='font-mono text-2xl font-bold leading-tight tracking-tight text-gray-900'>
              {key}
            </h1>
            <KindBadge kind={flag.kind} />
            <StatusBadge status={flag.status} />
          </div>
          {flag.description && (
            <p className='mt-2 max-w-3xl text-sm text-gray-600'>
              {flag.description}
            </p>
          )}
          {isExperiment && (
            <p className='mt-2 flex items-center gap-1.5 text-xs text-gray-500'>
              <ClockIcon className='h-4 w-4' aria-hidden='true' />
              Auswertung täglich gegen 07:00 UTC, Ergebnisse unten
            </p>
          )}
          <Explain className='mt-2' title='Was machen die Buttons?'>
            {ACTIONS_HELP}
          </Explain>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Button.SecondaryLG
            text='Bearbeiten'
            onClick={openEdit}
            disabled={readOnly}
          />
          {allowedActions(flag).map((action) =>
            action === ACTIONS.CONCLUDE ? (
              <Button.DangerLG
                key={action}
                text={actionLabel(action, flag)}
                onClick={() => openAction(action)}
                disabled={readOnly}
              />
            ) : (
              <Button.PrimaryLG
                key={action}
                text={actionLabel(action, flag)}
                onClick={() => openAction(action)}
                disabled={readOnly}
              />
            )
          )}
        </div>
      </header>

      {readOnly && (
        <Notice tone='error'>
          FEATURE_FLAGS enthält kein gültiges JSON. Speichern ist gesperrt,
          damit nichts überschrieben wird.
        </Notice>
      )}

      {locked && <LockNotice className='mb-2' />}

      <Card
        title='Konfiguration'
        description='Wo das Flag wirkt, wer teilnimmt und wie verteilt wird.'
        help={
          <>
            {HELP.unit}
            {HELP.surface}
            {HELP.salt}
          </>
        }
      >
        <dl className='grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3'>
          <Item label='Owner'>{flag.owner || '-'}</Item>
          <Item label='Unit'>{UNIT_LABELS[flag.unit] || flag.unit || '-'}</Item>
          <Item label='Surface'>
            {SURFACE_LABELS[flag.surface] || flag.surface || '-'}
          </Item>
          <Item label='Salt' mono>
            {currentSalt(flag)}
          </Item>
          <Item label='Verteilung'>{describeAllocation(flag) || '-'}</Item>
          <Item label='Gewinner' mono>
            {flag.winner || '-'}
          </Item>
          <Item label='Gestartet'>{formatDateTime(flag.started_at)}</Item>
          <Item label='Beendet'>{formatDateTime(flag.concluded_at)}</Item>
          <Item label='Laufzeit'>
            {days === null ? '-' : `${days} ${days === 1 ? 'Tag' : 'Tage'}`}
          </Item>
          <div className='sm:col-span-3'>
            <Item label='Targeting'>{describeTargeting(flag.targeting)}</Item>
          </div>
          <div className='sm:col-span-3'>
            <Item label='Overrides'>
              {overrides.length === 0 ? (
                '-'
              ) : (
                <ul className='space-y-0.5 font-mono text-xs'>
                  {overrides.map(([unitId, variant]) => (
                    <li key={unitId}>
                      {unitId}: {variant}
                    </li>
                  ))}
                </ul>
              )}
            </Item>
          </div>
        </dl>
      </Card>

      {!isExperiment && (
        <Card
          title='Ausspielung'
          description='Rollouts lassen sich jederzeit ändern und werden nicht statistisch ausgewertet.'
          help={HELP.percent}
        >
          <PercentControl
            key={`${flag.percent}`}
            flag={flag}
            onSave={savePercent}
            isSaving={isSaving}
            disabled={readOnly}
          />
        </Card>
      )}

      {isExperiment && (
        <Card
          title='Entscheidungskriterien'
          description={
            locked
              ? 'Vorab festgelegt und gesperrt. Danach entscheidet der Evaluator.'
              : 'Ab dem Start gesperrt. Danach entscheidet der Evaluator.'
          }
          help={
            <>
              {HELP.decision}
              {HELP.primary}
              {HELP.threshold}
            </>
          }
        >
          <dl className='grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3'>
            <Item label='Primäre Metrik'>
              {METRIC_LABELS[flag.decision?.primary] ||
                flag.decision?.primary ||
                '-'}
            </Item>
            <Item label='Laufzeit'>
              {formatNumber(flag.decision?.min_days)} bis{' '}
              {formatNumber(flag.decision?.max_days)} Tage
            </Item>
            <Item label='Schwelle P(best)'>
              {formatPercent(flag.decision?.threshold, 1)}
            </Item>
            <Item label='Exposures je Variante'>
              mindestens{' '}
              {formatNumber(flag.decision?.min_exposures_per_variant)}
            </Item>
            <Item label='Conversions insgesamt'>
              mindestens {formatNumber(flag.decision?.min_conversions_total)}
            </Item>
          </dl>
          {flag.unit !== 'visitor' && (
            <Notice tone='info' className='mt-4'>
              Der Evaluator wertet nur Experimente mit Unit visitor aus. Bei
              dieser Unit bleibt die Auswertung manuell.
            </Notice>
          )}
        </Card>
      )}

      {isExperiment && (
        <Card
          title='Ergebnis'
          description={
            resultsQuery.data?.updatedAt
              ? `Letzte Auswertung am ${formatDateTime(resultsQuery.data.updatedAt)}`
              : 'Aus EXPERIMENT_RESULTS, täglich 07:00 UTC.'
          }
        >
          {resultsQuery.isLoading ? (
            <Loader loadingText='Ergebnisse werden geladen ...' />
          ) : resultsQuery.isError ? (
            <Notice tone='error'>
              EXPERIMENT_RESULTS konnte nicht geladen werden.{' '}
              {extractErrorMessage(resultsQuery.error)}
            </Notice>
          ) : (
            <ExperimentResults flag={flag} result={result} />
          )}
        </Card>
      )}

      <Card
        title='JSON'
        description='Gespeicherter Stand des Flags, so wie Dashboard und Landingpage ihn lesen. Nur zum Nachsehen.'
      >
        <div className='overflow-x-auto text-xs'>
          <JSONTree
            data={flag}
            hideRoot
            shouldExpandNodeInitially={() => true}
          />
        </div>
      </Card>

      <FlagFormModal
        open={formOpen}
        setOpen={setFormOpen}
        session={formSession}
        saveFlags={saveFlags}
      />
      <StatusActionModal
        open={actionOpen}
        setOpen={setActionOpen}
        pending={pendingAction}
        onConfirm={confirmAction}
        isSaving={isSaving}
      />
    </div>
  )
}

export default Experiment
